import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Search, Award, MapPin } from "lucide-react";
import { homesAgents } from "@/lib/api";

export default function AgentsDirectory() {
  const [sp, setSp] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(sp.get("q") || "");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (sp.get("q")) params.q = sp.get("q");
    if (sp.get("state_slug")) params.state_slug = sp.get("state_slug");
    homesAgents(params).then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, [sp]);

  const submit = (e) => { e.preventDefault(); const n = new URLSearchParams(sp); if (q.trim()) n.set("q", q.trim()); else n.delete("q"); setSp(n); };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-10" data-testid="agents-directory">
      <h1 className="text-4xl font-black text-[#0F172A]">Find a real estate professional</h1>
      <p className="mt-2 text-slate-600">Verified local agents who know the specific city and neighborhood.</p>

      <form onSubmit={submit} className="relative mt-6 flex max-w-xl gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, brokerage, city or specialty"
            className="w-full rounded-full border border-slate-200 bg-white py-3 pl-12 pr-4 text-[#0F172A] outline-none focus:border-[#2563EB]" data-testid="agents-search" />
        </div>
        <button type="submit" className="rounded-full bg-[#2563EB] px-6 py-3 font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="agents-search-btn">Search</button>
      </form>

      <div className="mt-4 text-sm font-semibold text-[#0F172A]" data-testid="agents-count">{loading ? "Loading…" : `${data?.total ?? 0} agents`}</div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></div>
      ) : (
        <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="agents-grid">
          {(data?.agents || []).map((a) => (
            <Link key={a.id} to={`/homes/agents/${a.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-1 hover:shadow-lg" data-testid={`agent-card-${a.id}`}>
              <div className="flex items-center gap-4">
                {a.photo?.thumb_url ? <img src={a.photo.thumb_url} alt={a.name} className="h-16 w-16 rounded-full object-cover" /> : <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#0F172A] text-lg font-black text-white">{a.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>}
                <div className="min-w-0">
                  <div className="truncate text-lg font-bold text-[#0F172A]">{a.name}</div>
                  <div className="truncate text-sm text-slate-500">{a.brokerage}</div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3.5 w-3.5 text-[#2563EB]" /> {a.city_name}, {a.state_name}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(a.specialties || []).map((s) => <span key={s} className="rounded-full bg-[#EEF2FF] px-2.5 py-1 text-[11px] font-medium text-[#2563EB]">{s}</span>)}
              </div>
              <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-slate-500"><Award className="h-3.5 w-3.5 text-[#2563EB]" /> {a.years_experience} yrs · {(a.languages || []).join(", ")}</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
